import React from 'react';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Theme, makeStyles } from '../utility/styles';
import OptionsActionMenu from '../ui/OptionsActionMenu';
import { ISettingsBase, isGroup, makeLink } from './SettingsModel';
import { Link } from '../utility/Link';

const settingStyle = (theme: Theme) => ({
  root: {
    width: '100%',
    textDecoration: 'none',
    color: theme.palette.navBarSecondary.main,
    fontStyle: 'normal',
    padding: '0',
    margin: '0',
    outline: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    flex: 1,
    '& > p': {
      padding: '6px 0px',
    },
  },
  li: {
    padding: '0 0 0 0 !important',
  },
  selected: {
    backgroundColor: theme.palette.primary.dark,
    '&:hover, &:active, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
    fontStyle: 'italic',
  },
  listItemContainer: {
    listStyleType: 'none',
    '&:hover $listItemOptions': {
      transitionDelay: '35ms',
      transitionProperty: 'visibility',
      visibility: 'visible !important',
    },
  },
  listItemOptions: {
    top: 0,
    transform: 'none',
    height: '100%',
    visibility: 'hidden',
  },
  listItemOptionsTypography: {
    color: 'inherit',
  },
  listItemOptionsMenuItem: {
    '&:focus': {
      backgroundColor: `${theme.palette.dialogPrimary.main} !important`,
      color: `${theme.palette.dialogPrimary.contrastText} !important`,
      '& > svg': {
        color: `${theme.palette.dialogPrimary.contrastText} !important`,
      },
      '&:hover': {
        backgroundColor: `${theme.palette.dialogPrimary.main} !important`,
      },
    },
  },
  listItemOptionsMenuButton: {
    height: '100%',
    width: 'auto',
    padding: '0px 10px',
    marginLeft: '0px',
    backgroundColor: 'transparent !important',
    '&:hover, &:active': {
      color: theme.palette.navBarSecondary.light,
    },
  },
});

const useSettingsStyle = makeStyles(settingStyle);

const Setting = React.memo(({ settings, classes }: any) => {
  classes = useSettingsStyle({ classes });
  return !settings.access ? null : (
    <ListItem className={classes.li} classes={{ container: classes.listItemContainer }}>
      <Observe>
        {() => (
          <Link
            to={makeLink(settings)}
            style={{ paddingLeft: (settings.level() - 1) * 20 + 10 }}
            className={classNames(classes.root, { [classes.selected]: settings.selected })}
          >
            <Typography variant="body2">{settings.title}</Typography>
          </Link>
        )}
      </Observe>
      {!!settings.optionsMenu && (
        <ListItemSecondaryAction className={classes.listItemOptions}>
          <OptionsActionMenu
            title={settings.optionsMenu.title}
            tooltip={settings.optionsMenu.title}
            items={settings.optionsMenu.menuItems}
            classes={{
              menuItem: classes.listItemOptionsMenuItem,
              menuButton: classes.listItemOptionsMenuButton,
              listItemTypography: classes.listItemOptionsTypography,
            }}
          />
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
});

const settingsGroupStyle = (theme: Theme) => ({
  settingsGroupTitle: {
    color: theme.palette.navBarPrimary.main,
  },
  addButton: {
    display: 'block',
    width: '100%',
    textTransform: 'none',
    padding: '3px 8px 3px 30px',
    backgroundColor: 'transparent !important',
    '&:hover, &:active': {
      color: theme.palette.navBarSecondary.light,
      '& hr': {
        backgroundColor: theme.palette.navBarSecondary.light,
      },
    },
  },
  addButtonHidden: {
    display: 'none',
  },
  addButtonInnerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addButtonIcon: {
    marginRight: '2px',
  },
});

const useSettingsGroupStyle = makeStyles(settingsGroupStyle);

const SettingsGroup = React.memo(({ settings, classes, children }: any) => {
  classes = useSettingsGroupStyle({ classes });
  return (
    <Observe>
      {() => {
        const marginLeft = (settings.level() - 1) * 20;
        return (
          <Accordion
            square
            defaultExpanded={settings.expanded}
            expanded={settings.expanded()}
            onChange={(_evt: object, _expanded: boolean) => settings.expanded(!settings.expanded())}
          >
            <AccordionSummary
              className={classes.summary}
              style={{ paddingLeft: (settings.level() - 1) * 20 }}
              IconButtonProps={{ style: { marginLeft, top: '16px' } }}
            >
              <Typography className={classes.settingsGroupTitle} variant={settings.level() === 1 ? 'subtitle1' : 'subtitle2'}>
                {settings.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <>
                {!!settings.addButton && <SettingsGroupAddButton addButton={settings.addButton} classes={classes} />}
                {children}
              </>
            </AccordionDetails>
          </Accordion>
        );
      }}
    </Observe>
  );
});

const SettingsGroupAddButton = React.memo(({ addButton, classes }: any) => {
  return (
    <Observe>
      {() => {
        return (
          <Button
            className={classNames(classes.addButton, addButton.visible() ? undefined : classes.addButtonHidden)}
            arial-label={addButton.title}
            onClick={addButton.onClick}
          >
            <Divider />
            <div className={classes.addButtonInnerContainer}>
              <Typography variant="body2" className={classes.addGroupButtonTypography}>
                {addButton.title}
              </Typography>
              <AddCircleOutlineIcon className={classes.addButtonIcon} />
            </div>
            <Divider />
          </Button>
        );
      }}
    </Observe>
  );
});

export const SettingsOrGroup = React.memo(
  ({ settings, classes, refreshToggle }: { settings: ISettingsBase; classes: any; refreshToggle: boolean }) => (
    <Observe>
      {() => {
        if (isGroup(settings)) {
          return (
            <SettingsGroup settings={settings} classes={classes}>
              {settings.group.map((s) => (
                <SettingsOrGroup key={s.id} settings={s} classes={classes} refreshToggle={refreshToggle} />
              ))}
            </SettingsGroup>
          );
        }
        return <Setting settings={settings} />;
      }}
    </Observe>
  ),
);
